import React, { useCallback, useEffect, useReducer, useState } from "react";
import {
  Banner,
  Direction,
  Flex,
  LoadingIndicator,
  OverlayStyle,
  SlideoutDrawerV2,
  Text,
} from "@jsq/ds/core";
import { faySubscriptionClient } from "#faySubscriptionClient";
import { useWorkflowEventsSubscription } from "#graphql/supergraphHooks";
import {
  initialWorkflowNodes,
  nodeEventFromStreamed,
  selectRootAndChildren,
  workflowNodesReducer,
} from "#utils/workflowNodes";
import { ClassificationConfirm } from "../ClassificationConfirm";
import { ClassificationRow } from "../ClassificationRow";
import * as styles from "./styles_vanilla.css";
import type { IngestionState, WorkflowEventsSubscription } from "#graphql/supergraphTypes";
import type { OnDataOptions } from "@apollo/client";

export type SlideoutProps = {
  /** Ingestion (== workflow) id to subscribe to, or null when closed. */
  ingestionId: string | null;
  /** Ingestion state from the list query; drives live-tree vs confirm screen. */
  ingestionState?: IngestionState | null;
  onClose: () => void;
  /** Fired when the user approves the classification. Parent uses it to close
   *  the (opaque) drawer and refresh the coverage grid. */
  onApproved?: () => void;
  /** Arena the confirm screen's reads and writes are scoped to. */
  arenaId: number | undefined;
};

/**
 * Slideout — opens on an ingestion card click and subscribes to
 * `workflowEvents(workflowId: ingestionId, sinceId: "0")`, accumulating the
 * workflow's node tree (root + K child classification nodes) and rendering it
 * live.
 */
export const Slideout: React.FC<SlideoutProps> = ({
  ingestionId,
  ingestionState,
  onClose,
  onApproved,
  arenaId,
}) => {
  // Once classification finishes, fay flips the ingestion to
  // AWAITING_CONFIRMATION. We then swap the live node tree for the durable
  // confirm/approve screen, which reads `documentIngestion(id)`. After approve
  // the ingestion is COMPLETED (terminal): same durable screen, rendered
  // read-only as the deliverable-package summary.
  const isAwaitingConfirmation = ingestionState === "AWAITING_CONFIRMATION";
  const isComplete = ingestionState === "COMPLETED";
  // All node-tree reconciliation lives in the pure workflowNodesReducer; the
  // component just dispatches stream events into it.
  const [nodes, dispatch] = useReducer(workflowNodesReducer, initialWorkflowNodes);
  const [subError, setSubError] = useState<string | null>(null);
  // On error we stop the subscription (active=false) so it doesn't silently
  // churn in the background; "Try again" re-arms it. Toggling `skip` from true
  // back to false makes Apollo tear down the dead socket and open a fresh one.
  // Re-subscribing from sinceId="0" replays the full node tree, so no gap.
  const [active, setActive] = useState(true);

  // Clear the accumulated tree + error and re-arm the subscription. Shared by
  // the open-a-new-ingestion effect and the "Try again" affordance so the two
  // can't drift. Stable identity (no deps) keeps the subscription options below
  // referentially stable across renders — see the useCallback handlers.
  const rearm = useCallback(() => {
    dispatch({ kind: "reset" });
    setSubError(null);
    setActive(true);
  }, []);

  // Re-arm whenever we open a different ingestion (or close → null).
  useEffect(() => {
    rearm();
  }, [ingestionId, rearm]);

  // Stable handlers so the subscription options object doesn't change identity
  // each render, which would otherwise prompt Apollo to tear down and
  // re-establish the connection needlessly.
  const handleError = useCallback((err: { message?: string }) => {
    // Surface the failure instead of an infinite "Loading…" spinner, and stop
    // the subscription so it doesn't silently churn against a dead socket. The
    // user re-arms it via "Try again".
    setSubError(err.message || "Subscription error");
    setActive(false);
  }, []);

  const handleData = useCallback(({ data }: OnDataOptions<WorkflowEventsSubscription>) => {
    const event = nodeEventFromStreamed(data.data?.workflowEvents);
    if (event) {
      dispatch(event);
    }
  }, []);

  useWorkflowEventsSubscription({
    // Subscriptions don't federate, so they run on a dedicated client direct to
    // fay, not the host app's shared HTTP federation client.
    client: faySubscriptionClient,
    variables: { workflowId: ingestionId ?? "", sinceId: "0" },
    // No live tree once we're on the confirm/summary screen — it reads the
    // durable read model instead, so the subscription is pointless work.
    skip: !ingestionId || !active || isAwaitingConfirmation || isComplete,
    onError: handleError,
    onData: handleData,
  });

  const { root, children } = selectRootAndChildren(nodes);
  const isEmpty = root === undefined && children.length === 0;

  let headerTitle: string;
  if (isComplete) {
    headerTitle = "Completed classifications";
  } else if (isAwaitingConfirmation) {
    headerTitle = "Review classifications";
  } else {
    headerTitle = root?.summary ?? "Document ingestion";
  }

  return (
    <SlideoutDrawerV2
      isOpen={!!ingestionId}
      onClose={onClose}
      direction={Direction.Right}
      overlayStyle={OverlayStyle.Opaque}
    >
      <SlideoutDrawerV2.ComplexHeader title={headerTitle} />
      <SlideoutDrawerV2.Body>
        {/* The DS drawer is width: fit-content; this wrapper drives it (and the
            header, which is width: 100%) to half the viewport. */}
        <Flex flexDirection="column" className={styles.drawerBody}>
          {(isAwaitingConfirmation || isComplete) && ingestionId ? (
            // key={ingestionId} so opening a different ingestion is a fresh
            // mount — per-ingestion state resets without a reconciling effect.
            <ClassificationConfirm
              key={ingestionId}
              ingestionId={ingestionId}
              readOnly={isComplete}
              onApproved={onApproved}
              arenaId={arenaId}
            />
          ) : (
            <Flex flexDirection="column" gap={16} className={styles.streamingBody}>
              {/* Error while nothing has streamed: full empty-state with retry. */}
              {subError && isEmpty && (
                <Banner
                  variant="danger"
                  title="Couldn’t load workflow"
                  description={subError}
                  buttonProps={{
                    children: "Try again",
                    icon: "sync-alt",
                    onClick: rearm,
                  }}
                />
              )}
              {/* Error after partial data: a dismissible inline banner so the
              already-streamed nodes stay visible and the user knows the live
              feed dropped (and can re-arm it). */}
              {subError && !isEmpty && (
                <Banner
                  variant="warning"
                  title="Live updates paused"
                  description="The connection dropped. Showing the latest data received."
                  buttonProps={{
                    children: "Reconnect",
                    icon: "sync-alt",
                    onClick: rearm,
                  }}
                  onClear={() => setSubError(null)}
                />
              )}
              {!subError && isEmpty && (
                <Flex alignItems="center" gap={8}>
                  <LoadingIndicator size="small" />
                  <Text variant="secondary-md" asSpan>
                    Loading workflow…
                  </Text>
                </Flex>
              )}
              {/* Flat list — one row per document. The batch (root) lives in the
              header, so there's no nested tree: the meaningful units are the
              per-document classification nodes. */}
              {children.map((node) => (
                <ClassificationRow key={node.id} node={node} />
              ))}
            </Flex>
          )}
        </Flex>
      </SlideoutDrawerV2.Body>
    </SlideoutDrawerV2>
  );
};
